import { useMemo } from "react";
import PiCheckTickSquareSolid from "@markeroo/icons/src/solid/general/PiCheckTickSquareSolid";

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface PasswordRequirement {
  label: string;
  met: boolean;
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const requirements = useMemo((): PasswordRequirement[] => {
    return [
      { label: "At least 12 characters", met: password.length >= 12 },
      { label: "Contains uppercase letter", met: /[A-Z]/.test(password) },
      { label: "Contains lowercase letter", met: /[a-z]/.test(password) },
      { label: "Contains number", met: /\d/.test(password) },
      { label: "Contains special character", met: /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password) },
    ];
  }, [password]);

  const strength = useMemo(() => {
    const metCount = requirements.filter((r) => r.met).length;
    if (metCount === 0) return { level: 0, label: "", color: "bg-border" };
    if (metCount < 3) return { level: 1, label: "Weak", color: "bg-semantic-error" };
    if (metCount < 5) return { level: 2, label: "Fair", color: "bg-semantic-warning" };
    return { level: 3, label: "Strong", color: "bg-semantic-success" };
  }, [requirements]);

  if (!password) {
    return null;
  }

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div
          className="flex h-1.5 flex-1 gap-1"
          role="progressbar"
          aria-valuenow={strength.level * 33.33}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={strength.label ? `Password strength: ${strength.label}` : "Password strength indicator"}
        >
          {[1, 2, 3].map((level) => (
            <div
              key={level}
              className={`h-full flex-1 rounded-full transition-colors ${
                level <= strength.level ? strength.color : "bg-border"
              }`}
            />
          ))}
        </div>
        {strength.label && (
          <span
            className={`text-xs font-medium ${
              strength.level === 1
                ? "text-semantic-error"
                : strength.level === 2
                  ? "text-semantic-warning"
                  : "text-semantic-success-text"
            }`}
          >
            {strength.label}
          </span>
        )}
      </div>

      {/* Requirements checklist */}
      <ul className="space-y-1 text-xs">
        {requirements.map((req) => (
          <li
            key={req.label}
            className={`flex items-center gap-1.5 ${
              req.met ? "text-semantic-success-text" : "text-text-theme-muted"
            }`}
          >
            {req.met ? (
              <PiCheckTickSquareSolid size={14} />
            ) : (
              <div className="h-3.5 w-3.5 flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-current" />
              </div>
            )}
            <span>{req.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
